<template>
  <div class="notification-panel">
    <!-- ─── Header ─── -->
    <div class="notif-header px-5 pt-5 pb-0">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <span class="font-weight-bolder font-size-h6 text-dark">Thông báo</span>
        <span v-if="unreadCount > 0" class="badge badge-primary badge-pill px-3 ml-2">
          {{ unreadCount > 99 ? '99+' : unreadCount }} mới
        </span>
      </div>

      <!-- Tabs -->
      <div class="notif-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="notif-tab-btn"
          :class="{ 'is-active': activeTab === tab.key }"
          @click="switchTab(tab.key)"
        >
          {{ tab.label }}
          <span v-if="tab.key === 'unread' && unreadCount > 0" class="notif-tab-pill">
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- ─── List ─── -->
    <perfect-scrollbar class="notif-scroll">
      <!-- Initial loading -->
      <div v-if="loading && allNotifications.length === 0" class="d-flex justify-content-center py-10">
        <b-spinner variant="primary" />
      </div>

      <!-- Empty -->
      <div v-else-if="filteredNotifications.length === 0" class="notif-empty">
        <i class="flaticon2-bell-4" />
        <p class="mb-0">Không có thông báo nào</p>
      </div>

      <!-- Items -->
      <div v-else class="notif-list">
        <div
          v-for="item in filteredNotifications"
          :key="item.id"
          class="notif-item"
          :class="{ 'is-unread': !item.seen }"
          @click="handleClick(item)"
        >
          <!-- Avatar -->
          <div class="notif-avatar-wrap">
            <div class="notif-avatar" :style="avatarStyle(item)">
              {{ getInitial(item) }}
            </div>
          </div>

          <!-- Content -->
          <div class="notif-content">
            <div class="notif-title">
              <span v-if="getCreatorName(item)" class="creator-name">{{ getCreatorName(item) }}</span>
              {{ getTitleText(item) }}
            </div>
            <div v-if="getBody(item)" class="notif-body">
              {{ getBody(item) }}
            </div>
            <div class="notif-meta">
              <span class="notif-cat-label" :style="catLabelStyle(item)">{{ getCategoryLabel(item) }}</span>
              <span class="notif-time">{{ formatTime(item.createdAt) }}</span>
            </div>
          </div>

          <!-- Unread dot -->
          <span v-if="!item.seen" class="notif-unread-dot" />
        </div>

        <!-- Load more -->
        <div v-if="hasMore" class="notif-load-more">
          <b-button variant="light" size="sm" :disabled="loadingMore" @click.stop="loadMore">
            <b-spinner v-if="loadingMore" small class="mr-1" />
            Xem thêm
          </b-button>
        </div>
      </div>
    </perfect-scrollbar>

    <!-- ─── Footer ─── -->
    <div class="notif-footer">
      <span class="text-muted font-size-xs">{{ total }} thông báo</span>
      <a v-if="unreadCount > 0" href="#" @click.prevent="handleReadAll">Đọc tất cả</a>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import { resolveNotificationRoute } from '@/core/config/notificationRedirectMap';

const TITLE_TYPE_CREATOR = 2;
const PAGE_SIZE = 20;

const CATEGORY = Object.freeze({
  MENTION: 1,
  APPROVAL: 2,
  COMMENT: 3,
  SYSTEM: 4,
});

const CATEGORY_CONFIG = {
  [CATEGORY.MENTION]:  { label: 'Đề cập',    color: '#7239EA', bg: '#F8F5FF' },
  [CATEGORY.APPROVAL]: { label: 'Phê duyệt', color: '#009EF7', bg: '#F1FAFF' },
  [CATEGORY.COMMENT]:  { label: 'Bình luận', color: '#F1416C', bg: '#FFF5F8' },
  [CATEGORY.SYSTEM]:   { label: 'Hệ thống',  color: '#7E8299', bg: '#F5F8FA' },
};

const TABS = [
  { key: 'all',      label: 'Tất cả',     categoryFilter: null },
  { key: 'mention',  label: 'Đề cập',     categoryFilter: CATEGORY.MENTION },
  { key: 'approval', label: 'Phê duyệt',  categoryFilter: CATEGORY.APPROVAL },
];

export default {
  name: 'DropdownNotification',

  props: {
    value: { type: Number, default: 0 },
  },

  data() {
    return {
      allNotifications: [],
      total: 0,
      unreadCount: 0,
      page: 1,
      hasMore: false,
      loading: false,
      loadingMore: false,
      activeTab: 'all',
      tabs: TABS,
    };
  },

  computed: {
    filteredNotifications() {
      return this.allNotifications;
    },
  },

  mounted() {
    this.fetchNotifications();
    this.fetchUnreadCount();
  },

  methods: {

    buildParams(page) {
      const tab = TABS.find(t => t.key === this.activeTab);
      const params = { limit: PAGE_SIZE, page };
      if (tab && tab.categoryFilter != null) params.category = tab.categoryFilter;
      return params;
    },

    async fetchNotifications() {
      this.loading = true;
      try {
        const res = await ApiService.query('notification-events', {
          params: this.buildParams(1),
        });
        const { result, totalItems, totalPages, currentPage } = res.data.data;
        this.allNotifications = result;
        this.total = totalItems;
        this.page = currentPage;
        this.hasMore = currentPage < totalPages;
      } catch (_) {
        // silent
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      try {
        const nextPage = this.page + 1;
        const res = await ApiService.query('notification-events', {
          params: this.buildParams(nextPage),
        });
        const { result, totalPages, currentPage } = res.data.data;
        this.allNotifications.push(...result);
        this.page = currentPage;
        this.hasMore = currentPage < totalPages;
      } catch (_) {
        // silent
      } finally {
        this.loadingMore = false;
      }
    },

    switchTab(key) {
      if (this.activeTab === key) return;
      this.activeTab = key;
      this.allNotifications = [];
      this.page = 1;
      this.total = 0;
      this.hasMore = false;
      this.fetchNotifications();
    },

    async fetchUnreadCount() {
      try {
        const res = await ApiService.query('notification-events/unread-count');
        this.unreadCount = res.data.data.count || 0;
        this.$emit('input', this.unreadCount);
      } catch (_) {
        // silent
      }
    },

    async handleClick(item) {
      if (!item.seen) {
        item.seen = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.$emit('input', this.unreadCount);
        ApiService.patch(`notification-events/${item.id}/read`).catch(() => {});
      }
      const route = resolveNotificationRoute(item);
      if (route) this.$router.push(route).catch(() => {});
    },

    async handleReadAll() {
      try {
        await ApiService.post('notification-events/read-all');
        this.allNotifications.forEach(n => { n.seen = true; });
        this.unreadCount = 0;
        this.$emit('input', 0);
      } catch (_) {
        // silent
      }
    },

    // ─── Display helpers ───────────────────────────────────────────────────────

    getCategoryConfig(item) {
      const cat = item.notificationEvent && item.notificationEvent.category;
      return CATEGORY_CONFIG[cat] || CATEGORY_CONFIG[CATEGORY.SYSTEM];
    },
    getCategoryLabel(item) {
      return this.getCategoryConfig(item).label;
    },

    avatarStyle(item) {
      const cfg = this.getCategoryConfig(item);
      return { backgroundColor: cfg.bg, color: cfg.color };
    },

    catLabelStyle(item) {
      const cfg = this.getCategoryConfig(item);
      return { backgroundColor: cfg.bg, color: cfg.color };
    },

    getCreatorName(item) {
      const event = item.notificationEvent;
      if (!event || event.titleType !== TITLE_TYPE_CREATOR) return '';
      return (event.creator && event.creator.employee && event.creator.employee.fullName)
        || (event.creator && event.creator.username)
        || '';
    },

    getTitleText(item) {
      return (item.notificationEvent && item.notificationEvent.title) || '';
    },

    getBody(item) {
      return (item.notificationEvent && item.notificationEvent.body) || '';
    },

    // authorInitial(item) {
    //   const event = item.notificationEvent;
    //   const name = (event && event.creator && event.creator.employee && event.creator.employee.fullName)
    //     || (event && event.creator && event.creator.username);
    //   return name ? name.charAt(0).toUpperCase() : '?';
    // },

    getInitial(item) {
      const event = item.notificationEvent;
      const name = (event && event.creator && event.creator.employee && event.creator.employee.fullName)
        || (event && event.creator && event.creator.username);

      if (!name) return '?';
      return name
        .split(' ')
        .filter(Boolean)
        .slice(-2)
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    },

    formatTime(date) {
      if (!date) return '';
      return moment(date).locale('vi').fromNow();
    },
  },
};
</script>

<style lang="scss" src="./styles/dropdown-notification.scss" scoped></style>
